import {Component, OnInit} from '@angular/core';
import {DialogRef} from "@fundamental-ngx/core/dialog";
import {
    filenameDateFormat,
    IsBTPApplication,
    OperationalMode,
    OperationalScenario,
    pdfDateFormat,
    WizardUtils
} from "../../shared/WizardHelpers";
import {formatDate} from "@angular/common";
import {PdfService} from "../../../../../service/services/misc/pdf/pdf.service";
import {AppConfigService} from 'src/app/service/services/misc/app-config/app-config.service';
import {ZipFile, ZipService} from "../../../../../service/services/misc/zip/zip.service";
import {dump} from 'js-yaml';
import {SubaccountModel} from "../../../../../core/models/cis/Subaccount.model";
import {firstValueFrom} from "rxjs";
import {DialogService} from 'src/app/service/services/misc/dialog/dialog.service';
import {
    UnifiedCreationFinishedYamlConfirmationDialogComponent
} from "../unified-creation-finished-yaml-confirmation-dialog/unified-creation-finished-yaml-confirmation-dialog.component";
import {SolutionService} from "../../../../../extension/cloud-automation-designer/services/solution.service";
import {AutomationService} from "../../../../../service/services/rest/automation/automation.service";
import {ExecutionType} from "../unified-create-application-dialog.component";
import {
    ConfirmationDialogComponent,
    ConfirmationDialogConfig
} from "../../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component";
import {Utils} from "../../../../../core/helpers/Utils";

@Component({
    selector: 'app-unified-creation-finished-dialog',
    templateUrl: './unified-creation-finished-dialog.component.html',
    styleUrls: ['./unified-creation-finished-dialog.component.scss'],
    standalone: false
})
export class UnifiedCreationFinishedDialogComponent implements OnInit {
    readonly OperationalMode = OperationalMode;
    readonly OperationalScenario = OperationalScenario;
    readonly IsBTPApplication = IsBTPApplication;
    readonly ExecutionType = ExecutionType;
    res: any;
    rootPath: string;
    path: string;
    testResourceGroupPath: string;
    testFolderName: string;
    appDisplayName: string;
    payload: any;
    operationalMode: OperationalMode;
    isBTPApplication: IsBTPApplication;
    scenario: OperationalScenario;
    subaccount: SubaccountModel;
    templateSubaccount: SubaccountModel;
    downloadReport = false;
    isDownloadSectionVisible = false;
    isOpenInCadVisible = false;
    openInCad = true;
    cloudAutomationDesignerUrl = this.getCloudAutomationDesignerUrl(window.location.href);
    isDownloadedYamlFiles = false;
    executionType: ExecutionType;
    numberOfParameterIssues = 0;

    constructor(private _dialogRef: DialogRef, private _pdfService: PdfService, private _appConfigService: AppConfigService,
                private _zipService: ZipService, private _dialogService: DialogService, private _solutionService: SolutionService, private _automationService: AutomationService) {
    }

    ngOnInit(): void {
        this.res = this._dialogRef.data.res;
        this.payload = this._dialogRef.data.payload;
        this.rootPath = this._dialogRef.data.path;
        this.path = `${this.rootPath}/${this.payload.baseFolderName}`;
        this.operationalMode = this._dialogRef.data.operationalMode;
        this.isBTPApplication = this._dialogRef.data.isBTPApplication;
        this.subaccount = this._dialogRef.data.subaccount;
        this.templateSubaccount = this._dialogRef.data.templateSubaccount;
        this.appDisplayName = this._dialogRef.data.appDisplayName;
        this.scenario = this._dialogRef.data.operationalScenario;
        this.testFolderName = this.payload.testFolderName;
        this.testResourceGroupPath = `${this.path}/${this.payload.testFolderName}/${this.payload.testResourceGroupName}`;
        const isSAPManagedBTPUCAApp = this.scenario === OperationalScenario.UCA_LEASED || this.scenario === OperationalScenario.UCA_SPFI;
        this.isDownloadSectionVisible = isSAPManagedBTPUCAApp;
        this.isOpenInCadVisible = !!this._dialogRef.data.payload.automationSolution;
        this.executionType = this._dialogRef.data.executionType;
        if (this.res?.serviceInstanceParametersReport?.length > 0) {
            this.numberOfParameterIssues = WizardUtils.calculateNumberOfParameterIssues(this.res.serviceInstanceParametersReport);
            this.downloadReport = true;
        }
    }

    async onClose() {
        if (this.downloadReport) {
            await this.downloadEssentialFiles();
        }

        if (this.isDownloadSectionVisible && !this.isDownloadedYamlFiles) {
            await this.openYamlFilesConfirmationDialog();
        }

        if (this.openInCad && this.isOpenInCadVisible) {
            await this.openInCloudAutomationDesigner();
        }
    }

    async downloadEssentialFiles() {
        try {
            const serviceInstanceParametersReport = this.res?.serviceInstanceParametersReport;
            if (serviceInstanceParametersReport?.length > 0) {
                await this.downloadWithParametersReport(serviceInstanceParametersReport);
            }
        } catch (e) {
            //do nothing
        }
    }

    async downloadWithParametersReport(serviceInstanceParametersReport: any) {
        const parametersReportDefinition = WizardUtils.generateParametersReportDefinition(serviceInstanceParametersReport,
            this._dialogRef.data.payload.automationSolution.name, `${this.path}/${this.payload.resourceGroupName}`);
        const parametersReport = await this._pdfService.generatePdf(parametersReportDefinition);
        const formattedDate = formatDate(new Date(), filenameDateFormat, 'en-US');
        this._pdfService.downloadPdf(parametersReport, `${this.payload.managedServiceName}-parameters-report_${formattedDate}.pdf`);
    }

    async openInCloudAutomationDesigner() {
        const solutionPath = `${this.path}/${this.payload.resourceGroupName}`;
        const automationSolutionName = this._dialogRef.data.payload.automationSolution.name;
        try {
            const automationSolution = await firstValueFrom(this._automationService.getAutomationSolution(solutionPath, automationSolutionName));
            this._solutionService.loadSolution(automationSolution, true);
            window.open(`${this.cloudAutomationDesignerUrl}/designer`, '_blank', 'noopener,noreferrer');
        } catch (e) {
            window.open(`${this.cloudAutomationDesignerUrl}`, '_blank', 'noopener,noreferrer');
        }
    }

    async downloadYamlFiles() {
        this.isDownloadedYamlFiles = true;
        const content = [
            new ZipFile('level-0-app-tenant-instance.yaml', this.getResourceYaml()),
            new ZipFile('level-0-fulfillment-data.yaml', this.getFulfillmentDataYaml())
        ];
        try {
            await this._zipService.generateZip(content, 'level-0-testing.zip');
        } catch (e) {
            //do nothing
        }
    }

    getResourceYaml() {
        const data = {
            apiVersion: `${this.payload.resourceTypeDefinition.group}/${this.payload.resourceTypeDefinition.version}`,
            type: this.payload.resourceTypeDefinition.type,
            metadata: {
                name: "level-0-tenant-1",
                path: this.testResourceGroupPath
            }
        };
        return dump(data);
    }

    getFulfillmentDataYaml() {
        const region = this.templateSubaccount ? this.templateSubaccount.region : this.subaccount.region;
        const userInfo = this._appConfigService.getUserInfoModel();
        const data = WizardUtils.getFulfillmentDataYaml(this.testResourceGroupPath, this.payload.resourceTypeDefinition.group,
            this.payload.resourceTypeDefinition.version, this.payload.resourceTypeDefinition.type, userInfo.email,
            `${userInfo.first_name} ${userInfo.last_name}`, `cf-${region}`);
        return dump(data);
    }

    getCloudAutomationDesignerUrl(currentUrl: string) {
        const hashIndex = currentUrl.indexOf('app#/');
        const path = hashIndex !== -1 ? currentUrl.substring(hashIndex + 5) : currentUrl;
        const newPath = 'cloudAutomationDesigner';
        const convertedUrl = currentUrl.replace(path, newPath);
        return convertedUrl;
    }

    async openYamlFilesConfirmationDialog() {
        const dialogRef = this._dialogService.openDialog(UnifiedCreationFinishedYamlConfirmationDialogComponent, {}, undefined, "32rem");
        try {
            const res = await firstValueFrom(dialogRef.afterClosed);
            if (res.isDownloadSelected) {
                await this.downloadYamlFiles();
            }
        } catch {
            //do nothing
        }
    }
    async onDownloadReportChange() {
        if (this.downloadReport || !this.res?.serviceInstanceParametersReport?.length) {
            return;
        }

        // set 'downloadReport' back to 'true' so that checkbox is unchecked
        this.downloadReport = true;

        const confirmationDialogConfig = new ConfirmationDialogConfig({
            title: "Confirm PDF Download",
            bodyText: "Are you sure you want to skip the download of the parameter issues report for the generated AutomationSolution? <br/><br/> Note that the list of the detected issues won't be available anywhere after you close the wizard. You'll need to address these issues directly in the generated AutomationSolution for your solution to be complete.",
            cancelText: "Keep the PDF",
            confirmText: "Yes, Skip the PDF"
        });

        const dialogRef = this._dialogService.openDialog(ConfirmationDialogComponent, confirmationDialogConfig, undefined, "25rem");
        try {
            await firstValueFrom(dialogRef.afterClosed);
            // if user confirms, set 'downloadReport' to 'false' so that checkbox is unchecked
            this.downloadReport = false;
        } catch {
            return;
        }
    }

    protected readonly Utils = Utils;
}
